import type { Theme, Direction, CommonColors, ThemeProviderProps } from '@mui/material/styles';
import type { ThemeCssVariables } from './types';
import type { PaletteColorKey, PaletteColorNoChannels } from './core/palette';

// ----------------------------------------------------------------------

export type ThemeConfig = {
  direction: Direction;
  classesPrefix: string;
  cssVariables: ThemeCssVariables;
  defaultMode: ThemeProviderProps<Theme>['defaultMode'];
  modeStorageKey: ThemeProviderProps<Theme>['modeStorageKey'];
  fontFamily: Record<'primary' | 'secondary', string>;
  palette: Record<PaletteColorKey, PaletteColorNoChannels> & {
    common: Pick<CommonColors, 'black' | 'white'>;
    grey: {
      [K in 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 as `${K}`]: string;
    };
  };
};

export const themeConfig: ThemeConfig = {
  /** **************************************
   * Base
   *************************************** */
  defaultMode: 'light',
  modeStorageKey: 'theme-mode',
  direction: 'ltr',
  classesPrefix: 'minimal',
  /** **************************************
   * Css variables
   *************************************** */
  cssVariables: {
    cssVarPrefix: '',
    colorSchemeSelector: 'data-color-scheme',
  },
  /** **************************************
   * Typography
   *************************************** */
  fontFamily: {
    primary: "'Public Sans Variable', 'Public Sans', 'Segoe UI', sans-serif",
    secondary: "'Public Sans Variable', 'Public Sans', 'Segoe UI', sans-serif",
  },
  /** **************************************
   * Palette
   *************************************** */
  palette: {
    primary: {
      lighter: '#FCE1E8',
      light: '#F18AA1',
      main: '#E01E47',
      dark: '#B51639',
      darker: '#7A0D25',
      contrastText: '#FFFFFF',
    },
    secondary: {
      lighter: '#FDE9DF',
      light: '#F4A27F',
      main: '#ED7647',
      dark: '#BE5327',
      darker: '#7B3115',
      contrastText: '#FFFFFF',
    },
    info: {
      lighter: '#E7F7FB',
      light: '#9AD7E1',
      main: '#5EB1BF',
      dark: '#2E7886',
      darker: '#042A2B',
      contrastText: '#042A2B',
    },
    success: {
      lighter: '#D3FCD2',
      light: '#77ED8B',
      main: '#22C55E',
      dark: '#118D57',
      darker: '#065E49',
      contrastText: '#ffffff',
    },
    warning: {
      lighter: '#FFF5CC',
      light: '#FFD666',
      main: '#FFAB00',
      dark: '#B76E00',
      darker: '#7A4100',
      contrastText: '#1C252E',
    },
    error: {
      lighter: '#FFE9D5',
      light: '#FFAC82',
      main: '#FF5630',
      dark: '#B71D18',
      darker: '#7A0916',
      contrastText: '#FFFFFF',
    },
    grey: {
      50: '#F8FBFC',
      100: '#F1F7F8',
      200: '#E3EEF1',
      300: '#CCDDE0',
      400: '#A9BEC4',
      500: '#7B9299',
      600: '#556C73',
      700: '#314A50',
      800: '#16363B',
      900: '#042A2B',
    },
    common: {
      black: '#000000',
      white: '#FFFFFF',
    },
  },
};
